/**
 * CompareBadge — Phase 5.8b shared delta visual.
 *
 * Renders a small ▲/▼/● badge with the % delta. Three colour modes:
 *   "positive-good" — green up, red down (volume, attainment, score)
 *   "negative-good" — green down, red up (resolution time, breaches, FRT)
 *   "neutral"       — slate up/down (no value judgment)
 *
 * Returns null when the delta is undefined/null/NaN so callers don't need to
 * guard the comparison case themselves.
 */
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const TONE_CLASS = {
  good: "text-emerald-700 bg-emerald-50 border-emerald-200",
  bad: "text-rose-700 bg-rose-50 border-rose-200",
  flat: "text-slate-500 bg-slate-50 border-slate-200",
};

export default function CompareBadge({
  pct,
  mode = "positive-good",
  size = "xs",
  showLabel = false,
  label = "vs prev",
  unit = "%",
  testid,
}) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  const dir = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  const Icon = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  const tone = dir === "flat"
    ? "flat"
    : (mode === "positive-good")
      ? (dir === "up" ? "good" : "bad")
      : (mode === "negative-good")
        ? (dir === "up" ? "bad" : "good")
        : "flat";

  const sign = pct > 0 ? "+" : pct < 0 ? "" : "";
  const padding = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-1.5 py-0 text-[10px]";

  return (
    <span
      className={`inline-flex items-center gap-0.5 border rounded-full font-medium ${TONE_CLASS[tone]} ${padding}`}
      data-testid={testid || "compare-badge"}
      title={`${sign}${pct}${unit} ${label}`}>
      <Icon className="w-3 h-3" />
      {sign}{Math.abs(pct)}{unit}
      {showLabel && <span className="text-slate-500 font-normal ml-1">{label}</span>}
    </span>
  );
}
